import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Grid,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import theme from "../theme";
import { STORE_ID } from "../Config/Constants";

// Icons
import BrokenImageRoundedIcon from "@mui/icons-material/BrokenImageRounded";
import searchIcon from "../assets/icons/search.svg";
import CloseIcon from "@mui/icons-material/Close";

function AllServiceTicketsPage() {
  const navigate = useNavigate();
  const contextData = useContext(AutoTaskAuthContext) || {};
  const { userInfo, allKumuduDepartments, allInternalDepartments } =
    contextData;
  const userStore_id = userInfo.store_id;
  // console.log(userInfo);

  const [searchText, setSearchText] = useState("");
  const [serviceType, setServiceType] = useState(() =>
    userInfo.store_id === STORE_ID ? "External" : "Internal"
  );
  const [activeTab, setActiveTab] = useState(1);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  // Snackbar config
  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // Close Snackbar
  const handleClose = (event, reason) => {
    setSnackBarConfig((prevState) => ({ ...prevState, open: false }));
  };

  const tabRefs = useRef([]);
  const tabs = ["Internal", "Kumudu"];

  // Indicator position logic
  useEffect(() => {
    if (tabRefs.current[activeTab]) {
      const el = tabRefs.current[activeTab];
      setIndicatorStyle({
        left: el.offsetLeft,
        width: el.offsetWidth,
      });
    }
  }, [activeTab]);

  const handleServiceChange = (newValue) => {
    setServiceType(newValue);
  };

  const currentServices =
    serviceType === "external" ? allKumuduDepartments : allInternalDepartments;

  const filteredDepts =
    searchText !== ""
      ? currentServices?.filter((service) =>
          service.dept_name
            .trim()
            .toLowerCase()
            .includes(searchText.trim().toLowerCase())
        )
      : currentServices;

  return (
    <>
      {/* Custom Tabs (Internal, Kumudu) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          px: 1,
          py: 1,
          mt: 1,
          position: "relative",
          bgcolor: "#fff",
          borderRadius: 1,
        }}
      >
        {/* Animated indicator - placed first so it's underneath tabs */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            bottom: 8,
            background:
              "linear-gradient(90deg, rgba(0,128,128,1) 0%, rgba(68,40,160,1) 100%)",
            borderRadius: 1,
            transition: "all 0.3s ease",
            zIndex: 0, // stays behind text
            ...indicatorStyle,
          }}
        />

        {tabs.map((label, index) => {
          const isActive = activeTab === index;
          return (
            <Box
              key={label}
              ref={(el) => (tabRefs.current[index] = el)}
              onClick={() => {
                setActiveTab(index);
                handleServiceChange(index === 0 ? "internal" : "external");
              }}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                py: 1,
                position: "relative",
                zIndex: 1, // text stays above indicator
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 16, sm: 18, md: 20 },
                  fontWeight: 600,
                  color: isActive ? "white" : theme.lightTheme.placeholder,
                  transition: "color 0.3s ease",
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Search Bar */}
      <TextField
        placeholder="Search Department / Project"
        sx={{
          width: "100%",
          mt: 1,
          mb: 2,
          "& .MuiOutlinedInput-root": {
            height: "40px",
            fontSize: "14px",
            borderRadius: "50px",
            backgroundColor: "#fff",
            "&.Mui-focused fieldset": { borderColor: "#ccc" },
          },
          "& .MuiInputBase-input": {
            padding: "0 15px",
            height: "40px",
            lineHeight: "40px",
            boxSizing: "border-box",
          },
          "& .MuiInputBase-input::placeholder": {
            color: theme.textCol.subHeading,
            opacity: 1,
            fontSize: "12px",
            fontStyle: "italic",
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ marginRight: 0 }}>
              <img
                src={searchIcon}
                alt="search"
                style={{ width: 24, height: 24 }}
              />
            </InputAdornment>
          ),
          endAdornment: searchText ? (
            <InputAdornment position="end">
              <CloseIcon
                style={{ cursor: "pointer" }}
                onClick={() => setSearchText("")}
              />
            </InputAdornment>
          ) : null,
        }}
        variant="outlined"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />

      {/* Services List */}
      <Grid
        container
        spacing={{ xs: 2, sm: 3, md: 2 }}
        sx={{
          mt: { xs: 1, sm: 2, md: 3 },
          justifyContent: { xs: "flex-start", sm: "center", md: "center" },
          mb: 5,
        }}
      >
        {/* No Department Available */}
        {filteredDepts.length === 0 && (
          <Typography
            sx={{
              fontSize: 18,
              textAlign: "center",
              width: "100%",
              color: theme.lightTheme.placeholder,
            }}
          >
            No department found
          </Typography>
        )}

        {/* Coming Soon for Internal Ticket Feature */}
        {/* {serviceType === "internal" &&
          <Box sx={{
            mt: 25,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column"
          }}>
            <Typography sx={{ fontSize: 20 }}>Launching soon :)</Typography>
            <Box
              sx={{
                display: "inline-block",
                width: "1.5em",
                textAlign: "left",
                "::after": {
                  content: '""',
                  display: "inline-block",
                  whiteSpace: "pre",
                  animation: `${dotsAnimation} 1.5s steps(1, end) infinite`,
                },
              }}
            />
          </Box>
        } */}

        {/* External Tickets */}
        <Grid
          container
          spacing={2}
          rowSpacing={3}
          sx={{
            mx: 0,
            width: "100%",
            justifyContent: "flex-start",
            alignItems: "stretch",
          }}
          mb={2}
        >
          {filteredDepts?.map((service, index) => (
            <Grid
              key={index}
              item
              md={3}
              sm={3}
              xs={4}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                onClick={() =>
                  navigate("/raise-service-ticket", {
                    state: { service },
                  })
                }
                sx={{
                  // width: { xs: 115, sm: 150, md: 225 },
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  // justifyContent: { xs: "flex-start", sm: "center", md: "center" },
                  justifyContent: "center",
                  flexDirection: "column",

                  // pt: { xs: 2, sm: 0, md: 0 },
                  borderRadius: 3,
                  bgcolor: "#fff",
                  boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                  cursor: "pointer",
                  transition: "all 0.25s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    transform: "translateY(-5px)",
                  },
                  textAlign: "center",
                  minHeight: { xs: 140, md: 180 },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: { xs: 62, sm: 72, md: 82 },
                    height: { xs: 62, sm: 72, md: 82 },
                    borderRadius: service?.icon ? 0 : 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    bgcolor: service?.icon ? "transparent" : "#c6f5f3ff",
                    overflow: "hidden",
                  }}
                >
                  {service?.icon ? (
                    <Box
                      component="img"
                      src={service.icon}
                      alt="Service Icon"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <BrokenImageRoundedIcon
                      sx={{
                        fontSize: { xs: 24, sm: 36, md: 48 },
                        color: theme.lightTheme.placeholder,
                      }}
                    />
                  )}
                </Box>

                {/* Service Info */}
                <Typography
                  sx={{
                    fontSize: { xs: 12, sm: 14, md: 16 },
                    fontWeight: 600,
                    color: "black",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    textAlign: "center",
                    maxWidth: "100%",
                    whiteSpace: "normal",
                  }}
                >
                  {service.dept_name}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "56px !important", // force override snackbar position
          },
        }}
      >
        {/* You can use Alert for styled messages */}
        <Alert
          onClose={handleClose}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default AllServiceTicketsPage;
